import { HttpParams } from '@angular/common/http'
import { Injectable } from '@angular/core'
import { ActionHistoryData, AuthorizationData, AuthorizationHistory, AuthorizationHistoryModel, AuthorizationLikeItem, AuthorizationModel, filterAction } from '@mmx/shared'
import { from, map, Observable, switchMap } from 'rxjs'

import { AuthService } from '../auth.service'
import { AuthenticatedRestService } from '../auth-rest.service'
import { WebSocketService } from '../websocket.service'
import { SaveAuthorizationParams } from './models'

interface AuthQuestionAnswersParams {
  authorizationId: string
  portalId: string
}

interface PrepareFileUploadParams {
  contentType: string
  fileName: string
  documentType: string
}

export interface PrepareFileUploadResponse extends PrepareFileUploadParams {
  bucket: string
  object: string
  url: string
}

export interface AuthFileUpload {
  file: File
  documentType: string
}

export interface BulkCreateAuthorizationsResponse {
  authorizations: AuthorizationModel[]
  notLinkedCount: number
  totalCount: number
}

export interface AuthSearchHistoryResponse {
  histories: AuthorizationHistory[]
  appointments: { id: string, startAt: string }[]
  authorizations: AuthorizationData[]
  actionHistories: { action: string, data: any, createdAt: string }[]
  actionEvents: ActionHistoryData[]
  hasMore?: boolean
  cursor?: string | null
}

@Injectable({
  providedIn: 'root',
})
export class AppointmentAuthorizationService {
  constructor(
    private authService: AuthService,
    private api: AuthenticatedRestService,
    private websocketService: WebSocketService,
  ) {}

  public getAuthorizationList(patientId?: string): Observable<AuthorizationModel[]> {
    let route = `/authorizations`
    let params

    if (patientId) {
      params = new HttpParams({
        fromObject: {
          patientId,
        },
      })
    }

    return this.api.get<AuthorizationData[]>(route, params)
      .pipe(
        map(({ data }) => data.map((authorizationData) => new AuthorizationModel(authorizationData))),
      )
  }

  public getAuthorization(id: string, appointmentId: string) {
    const route = `/authorizations/${id}`
    const params = new HttpParams({
      fromObject: {
        appointmentId,
      },
    })

    return this.api.get<AuthorizationData>(route, params).pipe(
      map(({ data }) => new AuthorizationModel(data)),
    )
  }

  public createAuthorization(params: SaveAuthorizationParams) {
    const route = '/authorizations'

    return this.api.post<AuthorizationData>(route, params)
      .pipe(
        map(({ data }) => new AuthorizationModel(data)),
      )
  }

  public updateAuthorization(authorizationId: string, params: SaveAuthorizationParams) {
    const route = '/authorizations'

    return this.api.put<AuthorizationData>(route, {
      ...params,
      id: authorizationId,
    })
      .pipe(
        map(({ data }) => new AuthorizationModel(data)),
      )
  }

  public patchAuthorizationFields(authorizationId: string, manuallyEditedFields: string[]) {
    const route = '/authorizations/field'

    return this.api.patch<{ manuallyEditedFields: string[] }>(route, {
      id: authorizationId,
      manuallyEditedFields,
    })
      .pipe(
        map(({ data }) => data.manuallyEditedFields),
      )
  }

  public deleteAuthorization(authorizationId: string) {
    const route = `/authorizations/${authorizationId}`
    return this.api.delete(route)
  }

  public bulkCreateAuthorizations(
    appointmentId: string,
    carrierLookupId: string,
    patientInsuranceId: string,
    serviceReviews: AuthorizationLikeItem[],
  ): Observable<BulkCreateAuthorizationsResponse> {
    const route = '/authorizations/bulk'

    return this.api.post<{ authorizations: AuthorizationData[], notLinkedCount: number, totalCount: number }>(route, {
      appointmentId,
      carrierLookupId,
      patientInsuranceId,
      serviceReviews,
    })
      .pipe(
        map(({ data }) => ({
          authorizations: data.authorizations.map((authorizationData) => new AuthorizationModel(authorizationData)),
          notLinkedCount: data.notLinkedCount,
          totalCount: data.totalCount,
        })),
      )
  }

  public getAuthQuestionAnswers(params: AuthQuestionAnswersParams) {
    this.websocketService.messageQueue.next({
      action: 'auth.question-answers',
      ...params,
    })

    return this.websocketService.clinicianMessage$.pipe(
      filterAction('auth.question-answers'),
    )
  }

  public uploadAuthAttachments(authId: string, files: AuthFileUpload[]) {
    return this.prepareUpload(authId, files.map((f) => ({
      contentType: f.file.type,
      fileName: f.file.name,
      documentType: f.documentType,
      source: `clinician:${this.authService.user.id}`,
      sourceMethod: 'clinician-portal:authorization',
    })))
      .pipe(
        map(({ data }) => data),
        switchMap((filesDataWithUrl) => from(Promise.all(filesDataWithUrl.map((f) => {
          return this.uploadFile(f, files.find((file) => file.file.name === f.fileName).file)
        })))
          .pipe(
            map(() => filesDataWithUrl),
          )),
        switchMap((filesDataWithUrl: PrepareFileUploadResponse[]) => {
          const filesToProcess = filesDataWithUrl.filter((f) => !f.contentType.startsWith('text/')).map((f) => ({
            fileKey: f.object,
            bucket: f.bucket,
          }))

          if (!filesToProcess.length) {
            return from(filesDataWithUrl)
          }

          return this.postAttachmentsUpload(
            authId,
            filesToProcess,
          )
        }),
      )
  }

  public prepareUpload(authId: string, files: PrepareFileUploadParams[]) {
    return this.api.post<PrepareFileUploadResponse[]>(`/authorizations/${authId}/prepare-upload`, { files })
  }

  public getAttachments(authId: string) {
    return this.api.get(`/authorizations/${authId}/attachments`)
  }

  public postAttachmentsUpload(authId: string, files: { bucket: string, fileKey: string }[]) {
    return this.api.post(`/authorizations/${authId}/post-upload`, { files })
  }

  public deleteAttachments(authId: string, fileKeys: string[]) {
    return this.api.delete(`/authorizations/${authId}/attachments`, { params: this.api.convertToParams({ files: fileKeys }) })
  }

  private uploadFile({ url }: PrepareFileUploadResponse, file: File) {
    const blob = new Blob([file], { type: file.type })
    return fetch(url, { method: 'PUT', body: blob })
  }

  public cancelPullAuthStatus(authId) {
    return this.api.post(`/authorizations/pull-status-cancel/${authId}`, null)
  }

  public searchHistory(params: Record<string, any>) {
    return this.api.get<AuthSearchHistoryResponse>(
      `/authorizations/history-search`,
      this.api.convertToParams(params),
    )
      .pipe(
        map(({ data }) => ({
          appointments: data.appointments,
          histories: data.histories?.map((h) => new AuthorizationHistoryModel(h)),
          actionEvents: data.actionEvents,
          hasMore: data.hasMore === true,
          cursor: data.cursor ?? null,
        })),
      )
  }
}
